/**
 * /face-retouch — страница фото-ретуши.
 *
 * Разделение обязанностей:
 *   landmarks.js  — где лицо, где глаза и рот (+ загрузка модели с прогрессом)
 *   find-face.js  — лестница поиска, когда обычный проход лица не нашёл
 *   engine.js     — физика блика (разбор кадра + снятие)
 *   smooth.js     — выравнивание тона без потери пор
 *   features.js   — зубы и белок глаз
 *   tone.js       — свет и тон кожи
 *   skin-mask.js  — граница кожи по ЦВЕТУ, лейн «лицо не найдено»
 *   heal.js       — лечащая кисть (точечное удаление)
 *   этот файл     — UI, состояния, преобразование кадра, кап, экспорт, телеметрия
 *
 * Решения, которые видно в коде и стоит объяснить:
 *
 * 1. РАЗБОР КАДРА ДЕЛАЕТСЯ ОДИН РАЗ, ползунки его не пересчитывают. Модель
 *    (маска, цвет света, поле блика) не зависит от силы эффекта — сила это
 *    множитель на выходе. Поэтому перетаскивание ползунка стоит ~250 мс
 *    применения вместо ~1 с полного разбора, и картинка не «думает».
 *
 * 2. РАБОЧЕЕ РАЗРЕШЕНИЕ И ЭКСПОРТ РАЗНЫЕ. Разбор идёт на уменьшенной копии,
 *    а на сохранении поля модели растягиваются на полный размер (см.
 *    FREngine.rescaleModel — там объяснено, почему это не приближение).
 *
 * 3. ПРЕОБРАЗОВАНИЕ КАДРА (поворот, отражение, выравнивание) НЕ материализуется
 *    в промежуточный битмап. Оно живёт тремя числами и применяется при каждой
 *    отрисовке — и в превью, и в экспорте. Иначе поворот 12-мегапиксельного
 *    снимка каждым нажатием клал бы в память ещё 48 МБ, которых у телефона нет.
 *
 * 4. МАЗКИ КИСТИ ХРАНЯТСЯ В КООРДИНАТАХ ИСХОДНИКА, а не текущего кадра.
 *    Человек, поправивший прыщ и потом повернувший снимок, иначе увидел бы,
 *    как заплатка уехала на ухо. Прямое и обратное преобразование точки —
 *    fwdPoint/invPoint, они же обеспечивают переигрывание мазков на полном
 *    размере при сохранении.
 *
 * 5. СТРАНИЦА НИКОГДА НЕ МОЛЧИТ. Загрузка модели — полоса с байтами, поиск
 *    лица окнами — полоса с долей проб, применение — крутилка. Жалоба, с
 *    которой началась переделка, была именно про тишину.
 */
(function () {
    'use strict';

    var CFG = window.FR_CFG || {};
    var SLUG = CFG.slug || 'face-retouch';
    var I = CFG.i18n || {};

    var WORK_MAX = 1100;      // разрешение разбора
    var EXPORT_MAX = 8192;    // предохранитель холста; настоящий потолок — ось тира
    var ZOOMS = [1, 1.5, 2, 3, 4];

    var el = {};
    var state = {
        // Очередь снимков. Одиночный режим — её частный случай с одним
        // элементом: отдельной ветки «а если файл один» в состоянии нет,
        // иначе они разъедутся ровно там, где их перестанут проверять вместе.
        queue: [],        // [{file, limitKey, url, status}]
        idx: -1,          // какой снимок показан
        file: null, limitKey: null, srcBitmap: null,
        tf: { rot: 0, flipH: false, angle: 0 },
        workCanvas: null, workPlanes: null, healedPlanes: null,
        healOps: [],
        model: null, faces: [], lane: 'none',   // 'face' | 'color' | 'none'
        busy: false, pending: false, ready: false, saving: false,
        wipe: 0.5, cmp: 'wipe', zoom: 1, pan: { x: 0, y: 0 }, aim: null,
        deepTried: false, rotatedHint: 0,
    };
    function isBatch() { return state.queue.length > 1; }

    function $(id) { return document.getElementById(id); }

    /* ── Телеметрия ─────────────────────────────────────────────────────────
       Не «побольше событий», а ответы на вопросы, которые иначе решаются
       гаданием:
         · доходит ли человек от загрузки до сохранения и где отваливается;
         · сколько РЕАЛЬНО стоит разбор на его устройстве;
         · спасает ли лестница поиска те кадры, которые раньше отвечали
           «лицо не найдено» и заканчивались уходом;
         · трогает ли кто-нибудь новые возможности — кисть, поворот, свет;
         · как часто упирается ФИЗИКА: выбитые в белое блики и совпадение
           цвета света с цветом кожи. Это предел, а не дефект, но его частота
           решает, стоит ли вкладываться в генеративное дорисовывание.
       `stageOnce` не даёт одному человеку раздуть воронку повторами. */
    function ae(name, data) {
        try { window.__ae && window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (_) { }
    }
    var _stages = {};
    function stageOnce(name, data) {
        if (_stages[name]) return;
        _stages[name] = 1;
        ae('tool_funnel', Object.assign({ f: name }, data || {}));
    }

    document.addEventListener('DOMContentLoaded', function () {
        el.work = $('frWork'); el.before = $('frBefore'); el.after = $('frAfter');
        el.box = $('frBox'); el.pan = $('frPan'); el.wipe = $('frWipe'); el.truth = $('frTruth');
        el.busy = $('frBusy'); el.busyText = $('frBusyText'); el.save = $('frSave');
        el.faces = $('frFaces'); el.aimRing = $('frAim');
        el.aimHint = $('frAimHint');
        el.strip = $('frStrip'); el.stripTitle = $('frStripTitle'); el.stripRow = $('frStripRow');
        el.compare = $('frCompare'); el.rescue = $('frRescue');
        el.veil = $('frVeil'); el.veilText = $('frVeilText'); el.veilBar = $('frVeilBar');
        el.veilFill = $('frVeilFill'); el.veilSub = $('frVeilSub');
        el.eyesLocked = $('frEyesLocked'); el.eyesBody = $('frEyesBody');
        el.zoomVal = $('frZoomVal');
        el.fmt = $('frFmt'); el.size = $('frSize'); el.quality = $('frQuality');
        el.qualityOut = $('frQualityOut'); el.qualityWrap = $('frQualityWrap');
        el.sl = {
            shine: $('frShine'), smooth: $('frSmooth'), red: $('frRed'), eye: $('frEye'),
            teeth: $('frTeeth'), sclera: $('frSclera'),
            exposure: $('frExposure'), warmth: $('frWarmth'),
        };
        el.out = {
            shine: $('frShineOut'), smooth: $('frSmoothOut'), red: $('frRedOut'), eye: $('frEyeOut'),
            teeth: $('frTeethOut'), sclera: $('frScleraOut'),
            exposure: $('frExposureOut'), warmth: $('frWarmthOut'),
        };
        if (!el.work) return;

        bindUploader();
        bindTips();
        bindSliders();
        bindPresets();
        syncPresetPressed();
        bindTabs();
        bindStage();
        bindFrame();
        bindHeal();
        bindRescue();
        bindExport();
        watchModel();
        el.save && el.save.addEventListener('click', onSave);
        el.compare && el.compare.addEventListener('click', onCompare);
    });

    // ── Загрузка ────────────────────────────────────────────────────────────
    function bindUploader() {
        // Источник — событие общего аплоадера, а НЕ input.files: аплоадер
        // намеренно чистит input.value, чтобы повторный выбор того же файла
        // снова дал событие.
        //
        // И привязываемся через `__bindUploaderFile`, а не голым
        // addEventListener: файл может быть выбран РАНЬШЕ, чем отложенный
        // скрипт тула успел подписаться, и тогда событие уходит в пустоту.
        // Помощник вдобавок вычерпывает `el.__pendingFile`, то есть закрывает
        // именно эту гонку (сторож — UploaderListenerRaceTest).
        var zone = $('frUpload');
        var onFile = function (file, detail) {
            var files = (detail && detail.files && detail.files.length)
                ? Array.prototype.slice.call(detail.files)
                : (file ? [file] : []);
            if (files.length) addFiles(files);
        };
        if (typeof window.__bindUploaderFile === 'function') {
            window.__bindUploaderFile(zone, onFile);
        } else if (zone) {
            zone.addEventListener('tool-uploader:file', function (e) {
                onFile(e.detail && e.detail.file, e.detail);
            });
        }
        document.addEventListener('tool-uploader:clear', function () {
            for (var i = 0; i < state.queue.length; i++) revokeThumb(state.queue[i]);
            state.queue = []; state.idx = -1;
            state.file = null; state.model = null; state.ready = false;
            state.healOps = []; state.faces = []; state.lane = 'none';
            syncSave();
            renderStrip();
            el.work.hidden = true;
        });

        // Прогрев модели по первому признаку намерения. Ждать 3.7 МБ ПОСЛЕ
        // выбора файла — это те самые «6–21 секунды тишины»; ждать их, пока
        // человек тянет файл к зоне, не приходится вовсе.
        if (zone) {
            var warmed = false;
            var warm = function () {
                if (warmed) return; warmed = true;
                try { window.FRLandmarks && window.FRLandmarks.warm(CFG.sources || [], { numFaces: 6, mode: 'IMAGE', downloaderUrl: CFG.mdUrl }); } catch (_) { }
            };
            ['pointerenter', 'focusin', 'dragover', 'touchstart'].forEach(function (ev) {
                zone.addEventListener(ev, warm, { passive: true, once: true });
            });
        }
    }

    /**
     * Принять снимки. Второй выбор ДОБАВЛЯЕТ к очереди, а не заменяет её:
     * человек, набирающий пачку из разных папок, иначе теряет предыдущую
     * выборку без единого предупреждения.
     */
    function addFiles(files) {
        var added = 0;
        for (var i = 0; i < files.length; i++) {
            var f = files[i];
            var key = null;
            // Ключ капа штампуется ОДИН раз при добавлении и живёт с записью.
            // Выводить его из живого File на каждом нажатии нельзя: тул может
            // подменить объект (переворот по EXIF, конверсия HEIC), и тот же
            // снимок начнёт считаться новым — «2 файла в сутки» превратится
            // в «2 нажатия».
            try {
                key = (window.__toolLimit && window.__toolLimit.fileKey)
                    ? window.__toolLimit.fileKey(f) : null;
            } catch (_) { key = null; }
            // Тот же файл, выбранный дважды, — один элемент очереди: иначе он
            // и в полосе задвоится, и в отчёте «сохранено N из M».
            if (key && state.queue.some(function (q) { return q.limitKey === key; })) continue;
            state.queue.push({ file: f, limitKey: key, url: null, status: '' });
            added++;
        }
        if (!added) return;

        // Ранняя стена: очередь, которая не влезает в остаток дня, обязана
        // сказать об этом ДО нажатия, а не после минуты обработки.
        try {
            window.__toolLimit && window.__toolLimit.precheck &&
                window.__toolLimit.precheck(SLUG, state.queue.map(function (q) { return q.file; }));
        } catch (_) { }

        el.work.hidden = false;
        renderStrip();
        ae('feature_use', { f: 'queue_changed', n: state.queue.length, added: added });
        // Показываем первый ДОБАВЛЕННЫЙ снимок: если человек дослал файлы к
        // готовой пачке, прыжок на самый первый выглядел бы как сброс работы.
        selectPhoto(state.queue.length - added);
    }

    function revokeThumb(item) {
        if (item && item.url) { try { URL.revokeObjectURL(item.url); } catch (_) { } item.url = null; }
    }

    function removePhoto(i) {
        var item = state.queue[i];
        if (!item) return;
        revokeThumb(item);
        state.queue.splice(i, 1);
        ae('feature_use', { f: 'queue_remove', n: state.queue.length });
        if (!state.queue.length) {
            state.idx = -1; state.file = null; state.model = null;
            state.ready = false; syncSave();
            el.work.hidden = true;
            renderStrip();
            return;
        }
        try {
            window.__toolLimit && window.__toolLimit.precheck &&
                window.__toolLimit.precheck(SLUG, state.queue.map(function (q) { return q.file; }));
        } catch (_) { }
        renderStrip();
        selectPhoto(Math.min(i, state.queue.length - 1));
    }

    /** Полоса миниатюр. Рисуется только при пачке — на одном снимке она была
     *  бы рамкой вокруг очевидного. */
    function renderStrip() {
        if (!el.strip) return;
        if (!isBatch()) { el.strip.hidden = true; el.stripRow.innerHTML = ''; return; }
        el.strip.hidden = false;
        el.stripTitle.textContent = fmt(I.batch_count || '', { n: state.queue.length });
        el.stripRow.innerHTML = '';
        state.queue.forEach(function (item, i) {
            if (!item.url) item.url = URL.createObjectURL(item.file);
            // ⚠️ Две кнопки СОСЕДЯМИ в обёртке, а не крестик ВНУТРИ миниатюры.
            // Интерактивное в интерактивном — невалидная разметка, и
            // клавиатура с экранным диктором по ней ходят неправильно.
            var wrap = document.createElement('div');
            wrap.className = 'fr-thumb';

            var pick = document.createElement('button');
            pick.type = 'button';
            pick.className = 'fr-thumb-pick';
            pick.setAttribute('aria-current', i === state.idx ? 'true' : 'false');
            pick.title = item.file.name || '';
            pick.addEventListener('click', function () { selectPhoto(i); });

            var img = document.createElement('img');
            img.src = item.url; img.alt = item.file.name || '';
            pick.appendChild(img);

            if (item.status) {
                var dot = document.createElement('span');
                dot.className = 'fr-thumb-dot ' + item.status;
                pick.appendChild(dot);
            }
            wrap.appendChild(pick);

            var x = document.createElement('button');
            x.type = 'button';
            x.className = 'fr-thumb-x';
            x.setAttribute('aria-label', I.remove_photo || '×');
            // Видимый кружок отдельным элементом: зона нажатия обязана быть
            // 44 px (глобальный пол для касания), а КРУЖОК такого размера
            // занял бы половину миниатюры.
            var glyph = document.createElement('span');
            glyph.className = 'fr-thumb-x-glyph';
            glyph.textContent = '×';
            x.appendChild(glyph);
            x.addEventListener('click', function (e) { e.stopPropagation(); removePhoto(i); });
            wrap.appendChild(x);

            el.stripRow.appendChild(wrap);
        });
        syncSave();   // подпись кнопки зависит от размера очереди
    }

    /**
     * Отметить ОДНУ миниатюру, не перестраивая полосу.
     *
     * Полная перерисовка в цикле пачки сбрасывала бы горизонтальную прокрутку
     * полосы и клавиатурный фокус на каждом файле.
     */
    function markThumb(i, status) {
        if (!el.stripRow) return;
        var pick = el.stripRow.querySelectorAll('.fr-thumb-pick')[i];
        if (!pick) return;
        var dot = pick.querySelector('.fr-thumb-dot');
        if (!status) { if (dot) dot.remove(); return; }
        if (!dot) { dot = document.createElement('span'); pick.appendChild(dot); }
        dot.className = 'fr-thumb-dot ' + status;
    }

    /** Подстановка :плейсхолдеров — те же имена, что в PHP-строках. */
    function fmt(tpl, vals) {
        return String(tpl || '').replace(/:(\w+)/g, function (m, k) {
            return Object.prototype.hasOwnProperty.call(vals, k) ? String(vals[k]) : m;
        });
    }
    function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

    /* Снимок с поворотом из EXIF. Safari до 17, Chrome до 111 и Firefox до 111
       значения 'from-image' не знают и бросают TypeError: открытие снимка
       отвечало «не удалось прочитать файл», пакетный разбор падал. Их
       умолчание и было «как в EXIF» (замер: EXIF-6 без опции даёт 1044×1400
       на Chromium 110, Firefox 108 и WebKit 16.4) — повтор без опции ничего
       не теряет. Образец — orientedBitmap в /image-compressor. */
    function orientedBitmap(file) {
        return createImageBitmap(file, { imageOrientation: 'from-image' })
            .catch(function () { return createImageBitmap(file); });
    }

    function selectPhoto(i) {
        var item = state.queue[i];
        if (!item) return;
        state.idx = i;
        renderStrip();
        load(item.file, item.limitKey);
    }

    async function load(file, limitKey) {
        state.file = file;
        state.limitKey = limitKey || null;   // проштампован при добавлении в очередь
        state.ready = false; syncSave();     // новый файл — готовности ещё нет
        state.t0 = performance.now();
        // Новый снимок — новый кадр и новая правка: чужие мазки и чужой
        // поворот на нём означали бы заплатку посреди другого лица.
        state.tf = { rot: 0, flipH: false, angle: 0 };
        state.healOps = []; state.healedPlanes = null;
        state.deepTried = false; state.rotatedHint = 0; state.lane = 'none';
        state.modelFailed = false;
        setAim(null); setZoom(1);
        syncFrameControls(); syncHealControls();
        _stages = {};   // новый файл — новая воронка

        showVeil(I.loading || '');
        try {
            state.srcBitmap = await orientedBitmap(file);
        } catch (err) {
            hideVeil();
            window.__handleError && window.__handleError(err, SLUG, { say: I.err_read || 'error' });
            ae('tool_error', { f: 'decode_failed', msg: String(err && err.message).slice(0, 120) });
            return;
        }

        stageOnce('picked', {
            mb: Math.round((file.size || 0) / 1048576),
            ext: (file.name || '').split('.').pop().slice(0, 6).toLowerCase(),
        });
        el.work.hidden = false;
        prepareWorkCanvas();
        drawBefore();
        await refresh(true);
        stageOnce('ready', {
            w: state.srcBitmap.width, h: state.srcBitmap.height,
            faces: state.faces.length, lane: state.lane,
            // Сколько разбор стоил НА ЭТОМ устройстве — единственный способ
            // узнать, терпимо ли это на телефоне.
            ms: Math.round(performance.now() - state.t0),
            mp: +((state.srcBitmap.width * state.srcBitmap.height) / 1e6).toFixed(1),
        });
    }

    /**
     * Полный цикл после смены кадра: разбор + применение.
     *
     * ⚠️ Отказ разбора — это ИСХОД, а не выход из функции. Раньше здесь стоял
     * `return`, и при недоступной модели (нет сети, вырезан CDN) человек
     * получал снимок слева, ПУСТУЮ половину справа и ни строчки объяснения:
     * строка честности не рисовалась вовсе, кнопки спасения не появлялись, а
     * тост жил секунды. При этом свет, кадр и кисть работали бы — просто об
     * этом неоткуда было узнать.
     */
    async function refresh(detect) {
        try {
            if (detect) await analyze();
        } catch (err) {
            state.modelFailed = true;
            // Отказ детектора уже объяснён строкой вердикта (truth_no_model) —
            // тост с сырым текстом wasm («Aborted()…») поверх него только пугает.
            var unavailable = !!(window.FRLandmarks && window.FRLandmarks.isUnavailable && window.FRLandmarks.isUnavailable(err));
            if (!unavailable) window.__handleError && window.__handleError(err, SLUG);
            ae('tool_error', { f: 'analyze_failed', reason: unavailable ? 'detector' : undefined, msg: String(err && err.message).slice(0, 120) });
            applyFaces([], 'none');   // назовёт причину и откроет двери
        }
        hideVeil();
        await apply();
    }

    // ── Преобразование кадра ────────────────────────────────────────────────
    /** Размер кадра ПОСЛЕ поворота на 90°-кратный угол. */
    function baseSize(bm) {
        var swap = state.tf.rot === 90 || state.tf.rot === 270;
        return { w: swap ? bm.height : bm.width, h: swap ? bm.width : bm.height };
    }
    /**
     * Выравнивание поворачивает снимок ВНУТРИ той же рамки, поэтому углы
     * ушли бы в прозрачность. Увеличение на этот множитель держит рамку
     * заполненной — так же ведёт себя редактор в телефоне, и это ожидаемое
     * поведение, а не четыре серых треугольника, которые надо обрезать.
     */
    function coverScale(bw, bh, deg) {
        if (!deg) return 1;
        var a = Math.abs(deg * Math.PI / 180);
        var c = Math.abs(Math.cos(a)), s = Math.abs(Math.sin(a));
        return Math.max((bw * c + bh * s) / bw, (bw * s + bh * c) / bh);
    }
    function paintTransformed(ctx, bm, outW, outH) {
        var b = baseSize(bm);
        ctx.setTransform(outW / b.w, 0, 0, outH / b.h, 0, 0);
        ctx.translate(b.w / 2, b.h / 2);
        if (state.tf.angle) ctx.rotate(state.tf.angle * Math.PI / 180);
        var k = coverScale(b.w, b.h, state.tf.angle);
        if (k !== 1) ctx.scale(k, k);
        if (state.tf.rot) ctx.rotate(state.tf.rot * Math.PI / 180);
        if (state.tf.flipH) ctx.scale(-1, 1);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bm, -bm.width / 2, -bm.height / 2, bm.width, bm.height);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    /** Точка исходника (доли) → точка текущего кадра (доли). */
    function fwdPoint(bm, nx, ny) {
        var b = baseSize(bm);
        var x = (nx - 0.5) * bm.width, y = (ny - 0.5) * bm.height;
        if (state.tf.flipH) x = -x;
        var r = state.tf.rot * Math.PI / 180, cr = Math.cos(r), sr = Math.sin(r);
        var x1 = x * cr - y * sr, y1 = x * sr + y * cr;
        var k = coverScale(b.w, b.h, state.tf.angle);
        x1 *= k; y1 *= k;
        var a = state.tf.angle * Math.PI / 180, ca = Math.cos(a), sa = Math.sin(a);
        return { x: (x1 * ca - y1 * sa + b.w / 2) / b.w, y: (x1 * sa + y1 * ca + b.h / 2) / b.h, k: k };
    }
    /** Точка текущего кадра (доли) → точка исходника (доли). */
    function invPoint(bm, tx, ty) {
        var b = baseSize(bm);
        var x = (tx - 0.5) * b.w, y = (ty - 0.5) * b.h;
        var a = -state.tf.angle * Math.PI / 180, ca = Math.cos(a), sa = Math.sin(a);
        var x1 = x * ca - y * sa, y1 = x * sa + y * ca;
        var k = coverScale(b.w, b.h, state.tf.angle);
        x1 /= k; y1 /= k;
        var r = -state.tf.rot * Math.PI / 180, cr = Math.cos(r), sr = Math.sin(r);
        var x2 = x1 * cr - y1 * sr, y2 = x1 * sr + y1 * cr;
        if (state.tf.flipH) x2 = -x2;
        return { x: (x2 + bm.width / 2) / bm.width, y: (y2 + bm.height / 2) / bm.height };
    }

    function prepareWorkCanvas() {
        var b = baseSize(state.srcBitmap);
        var k = Math.min(1, WORK_MAX / Math.max(b.w, b.h));
        var w = Math.max(1, Math.round(b.w * k)), h = Math.max(1, Math.round(b.h * k));
        var c = state.workCanvas || (state.workCanvas = document.createElement('canvas'));
        c.width = w; c.height = h;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        paintTransformed(ctx, state.srcBitmap, w, h);
        state.workPlanes = window.FREngine.toPlanes(ctx.getImageData(0, 0, w, h));
        state.healedPlanes = null;
    }

    /** Плоскости с уже наложенными мазками кисти — вход всей цепочки ретуши. */
    function sourcePlanes() {
        if (!state.healOps.length) return state.workPlanes;
        if (!state.healedPlanes) {
            state.healedPlanes = window.FRHeal.apply(state.workPlanes, mappedOps(), { mask: state.model && state.model.mask });
        }
        return state.healedPlanes;
    }
    /** Мазки, переведённые из координат исходника в координаты текущего кадра. */
    function mappedOps() {
        var bm = state.srcBitmap;
        return state.healOps.map(function (op) {
            var p = fwdPoint(bm, op.x, op.y);
            return { x: p.x, y: p.y, r: op.r * p.k };
        });
    }

    function drawBefore() {
        var c = el.before, p = state.workPlanes;
        c.width = p.w; c.height = p.h;
        c.getContext('2d').putImageData(window.FREngine.toImageData(p), 0, 0);
        el.after.width = p.w; el.after.height = p.h;
        // Коробка принимает пропорции снимка — иначе высокий портрет упирается
        // в max-height и object-fit дорисовывает поля по бокам.
        el.box.style.aspectRatio = p.w + ' / ' + p.h;
        el.box.style.maxHeight = '58vh';
        el.box.style.margin = '0 auto';
        el.box.style.width = 'min(100%, calc(58vh * ' + (p.w / p.h) + '))';
        setWipe(state.wipe);
    }

    // ── Разбор ──────────────────────────────────────────────────────────────
    async function analyze() {
        showVeil(I.loading || '');
        await window.FRLandmarks.ensure(CFG.sources || [], { numFaces: 6, mode: 'IMAGE', downloaderUrl: CFG.mdUrl });
        state.modelFailed = false;
        showVeil(I.analyzing || '', true);
        var p = state.workPlanes;
        var res = await window.FRFind.search(state.workCanvas, p.w, p.h, { deep: false });

        // Обычный проход промахнулся — идём лестницей окон. Это ТА САМАЯ
        // жалоба: «пишет, что лицо не обнаружено», при том что лицо в кадре
        // есть и человек его видит.
        if (!res.faces.length) {
            state.deepTried = true;
            showVeil(I.searching || '', true, 0);
            res = await window.FRFind.search(state.workCanvas, p.w, p.h, {
                deep: true,
                onStep: function (done, total) { veilProgress(done, total); },
            });
            ae('feature_use', {
                f: 'deep_search', found: res.faces.length, via: res.via, tries: res.tries,
                ms: Math.round(performance.now() - state.t0),
            });
        }
        state.rotatedHint = res.rotated || 0;
        applyFaces(res.faces, res.faces.length ? 'face' : 'none');
    }

    /** Общий хвост любого способа найти лицо (обычный, лестница, указание). */
    function applyFaces(faces, lane) {
        state.faces = faces || [];
        state.lane = lane;
        if (!state.faces.length) {
            state.model = null; state.ready = false;
            renderFaceChips(); showTruth(null); syncSave(); syncTabs();
            ae('tool_diagnostic', { issue: 'no_face', deep: state.deepTried ? 1 : 0 });
            return;
        }
        var opts = { faceWidth: state.faces[0].box.w };
        state.model = window.FREngine.analyze(state.workPlanes, state.faces, opts);
        state.model.faceWidth = opts.faceWidth;
        state.healedPlanes = null;   // маска изменилась — мазки переигрываем
        state.ready = !!(state.model && state.model.ok);
        renderFaceChips(); showTruth(state.model); syncSave(); syncTabs();
        ae('feature_use', {
            f: 'analyzed', faces: state.faces.length, lane: lane,
            sep: state.model.separability ? +state.model.separability.toFixed(3) : 0,
            blown: state.model.blownFrac ? +(state.model.blownFrac * 100).toFixed(1) : 0,
            lightOk: state.model.light ? (state.model.light.confident ? 1 : 0) : 0,
        });
    }

    /** Лейн «по цвету кожи»: границу задаёт оттенок, а не геометрия лица. */
    function colorLane() {
        var p = state.workPlanes;
        var got = window.FRSkinMask.build(p, {});
        if (!got.blobs || got.coverage < 0.004) {
            window.__toast && window.__toast(I.no_face_hint || '', 'warning');
            ae('tool_diagnostic', { issue: 'color_lane_empty', cov: +(got.coverage * 100).toFixed(2) });
            return false;
        }
        state.faces = [];
        state.lane = 'color';
        state.model = window.FREngine.analyze(p, [], { mask: got.mask });
        if (state.model) state.model.faceWidth = 0.35;   // масштаб пятен: считаем лицо крупным планом
        state.healedPlanes = null;
        state.ready = !!(state.model && state.model.ok);
        renderFaceChips(); showTruth(state.model); syncSave(); syncTabs();
        ae('feature_use', { f: 'color_lane', cov: +(got.coverage * 100).toFixed(2), blobs: got.blobs });
        return true;
    }

    function params() {
        var v = function (k, d) { return el.sl[k] ? (+el.sl[k].value) : d; };
        return {
            shine: v('shine', 0) / 100,
            smooth: v('smooth', 0) / 100,
            red: v('red', 0) / 100,
            eye: v('eye', 0) / 100,
            teeth: v('teeth', 0) / 100,
            sclera: v('sclera', 0) / 100,
            exposure: v('exposure', 0) / 100,
            warmth: v('warmth', 0) / 100,
        };
    }

    /** Применение к плоскостям — одна цепочка и для превью, и для экспорта. */
    function pipeline(planes, model, faces, pr, faceWidth) {
        var out;
        if (model && model.ok) {
            out = window.FREngine.deshine(planes, model, { amount: pr.shine });
            if (pr.smooth > 0 || pr.red > 0) {
                out = window.FRSmooth.smoothSkin(out, model, {
                    strength: pr.smooth, redness: pr.red, faceWidth: faceWidth,
                });
            }
            // Круги под глазами, зубы и белок — только там, где есть ГЕОМЕТРИЯ.
            // В лейне «по цвету» лиц нет вовсе, и эти три возможности не
            // «работают хуже», их просто нет — так и написано на вкладке.
            if (faces && faces.length) {
                if (pr.eye > 0) out = window.FRSmooth.brightenUnderEye(out, model, faces, { amount: pr.eye });
                if (window.FRFeatures) {
                    if (pr.teeth > 0) out = window.FRFeatures.whitenTeeth(out, faces, { amount: pr.teeth });
                    if (pr.sclera > 0) out = window.FRFeatures.brightenEyes(out, faces, { amount: pr.sclera });
                }
            }
        } else {
            // Лица нет — но свет и тон работать обязаны: это единственные две
            // правки, которым геометрия не нужна, и отказывать в них значило
            // бы оставлять человека с мёртвой страницей.
            out = window.FRHeal.clonePlanes(planes);
        }
        if (pr.exposure || pr.warmth) {
            // whole:true — фото ЯВНО разрешает работу по всему кадру, когда
            // маски нет. У видео это было бы неверно (см. tone.js).
            window.FRTone.apply(out, model, { exposure: pr.exposure, warmth: pr.warmth, whole: true });
        }
        return out;
    }

    async function apply() {
        if (!state.srcBitmap) return;
        if (state.busy) { state.pending = true; return; }
        state.busy = true;
        setBusy(true, I.applying || '');
        // Отдаём кадр браузеру, иначе индикатор занятости не успевает
        // нарисоваться до синхронной работы и «крутилка» не видна вовсе.
        await new Promise(function (r) { requestAnimationFrame(function () { setTimeout(r, 0); }); });
        try {
            var src = sourcePlanes();
            var res = pipeline(src, state.model, state.faces, params(),
                state.model ? state.model.faceWidth : 0.35);
            el.after.getContext('2d').putImageData(window.FREngine.toImageData(res), 0, 0);
        } catch (err) {
            window.__handleError && window.__handleError(err, SLUG);
            ae('tool_error', { f: 'apply_failed', msg: String(err && err.message).slice(0, 120) });
        } finally {
            state.busy = false;
            setBusy(false);
            // ⚠️ Готовность пересчитывается ЗДЕСЬ, а не только после разбора.
            // На снимке без лица кнопку зажигает сама правка — свет, тон, мазок
            // кисти, поворот, — и она приходит движением ползунка, у которого
            // своей ветки syncSave() нет. Замерено сторожем: свет менял кадр на
            // 100%, а «Сохранить фото» оставалась погашенной.
            syncSave();
            if (state.pending) { state.pending = false; apply(); }
        }
    }

    // ── Управление ──────────────────────────────────────────────────────────
    var applyTimer = null;
    function scheduleApply() {
        clearTimeout(applyTimer);
        applyTimer = setTimeout(apply, 90);
    }

    function slLabel(k, v) {
        // Свет и тон ходят в обе стороны от нуля: знак у них — это смысл
        // («теплее» против «холоднее»), а проценты читались бы как сила.
        if (k === 'exposure' || k === 'warmth') return (v > 0 ? '+' : '') + v;
        return v + '%';
    }
    function bindSliders() {
        Object.keys(el.sl).forEach(function (k) {
            var input = el.sl[k];
            if (!input) return;
            window.__toolSettings && window.__toolSettings.bind(SLUG, input, 'sl_' + k, input.value);
            var sync = function () {
                if (el.out[k]) el.out[k].textContent = slLabel(k, +input.value);
                clearPresetPressed();
                // Какой ползунок трогали — по одному событию на ползунок за
                // сессию: интересен ФАКТ обращения, а не число движений.
                stageOnce('slider_' + k, { v: +input.value });
                scheduleApply();
            };
            input.addEventListener('input', sync);
            if (el.out[k]) el.out[k].textContent = slLabel(k, +input.value);
        });
    }

    var PRESETS = {
        light: { shine: 55, smooth: 20, red: 15, eye: 15, teeth: 0, sclera: 0 },
        // Зубы 45/80 — см. пресеты /video-retouch: числа подбирались под
        // прежнее отбеливание, которое светлоту почти не поднимало.
        natural: { shine: 100, smooth: 45, red: 35, eye: 30, teeth: 45, sclera: 25 },
        // Блеск 150 % — см. FREngine.deshine: выше 100 % досниваются вершины бликов.
        strong: { shine: 150, smooth: 85, red: 70, eye: 60, teeth: 80, sclera: 55 },
    };
    /** Нажат тот пресет, чьи значения реально стоят (см. то же в /video-retouch). */
    function syncPresetPressed() {
        var hit = null;
        Object.keys(PRESETS).some(function (name) {
            var p = PRESETS[name];
            var same = Object.keys(p).every(function (k) { return !el.sl[k] || +el.sl[k].value === p[k]; });
            if (same) hit = name;
            return same;
        });
        var b = document.querySelectorAll('.fr-preset');
        for (var i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', b[i].dataset.preset === hit ? 'true' : 'false');
    }
    function clearPresetPressed() {
        var b = document.querySelectorAll('.fr-preset');
        for (var i = 0; i < b.length; i++) b[i].setAttribute('aria-pressed', 'false');
    }
    function bindPresets() {
        var host = $('frPresets');
        if (!host) return;
        host.addEventListener('click', function (e) {
            var btn = e.target.closest('.fr-preset');
            if (!btn) return;
            var p = PRESETS[btn.dataset.preset];
            if (!p) return;
            clearPresetPressed();
            btn.setAttribute('aria-pressed', 'true');
            Object.keys(p).forEach(function (k) {
                if (!el.sl[k]) return;
                el.sl[k].value = p[k];
                if (el.out[k]) el.out[k].textContent = slLabel(k, p[k]);
                window.__toolSettings && window.__toolSettings.save(SLUG, 'sl_' + k, String(p[k]));
            });
            ae('tool_interact', { f: 'preset', name: btn.dataset.preset });
            scheduleApply();
        });
    }

    /**
     * Живая строка подсказки под ползунками.
     *
     * Тексты объясняют главное — ЧТО именно делает ползунок и чего он НЕ
     * делает («снимается отражение источника, а не яркость кожи»), и выбросить
     * их было нельзя. Но напечатанные все сразу, они занимали 405 px в рейке
     * шириной 286 — прибор переставал помещаться в экран. Поэтому текст один и
     * тот, который сейчас нужен: строка встаёт на элементе, которого касаются,
     * и возвращается к сводке вкладки, когда его отпускают.
     *
     * Слушаем и `pointerenter`, и `focus`, и `input`: у мыши подсказка нужна
     * ДО нажатия, у пальца её показывает только само движение, а клавиатура
     * ходит фокусом. Один способ из трёх оставил бы без объяснений целый класс
     * ввода.
     */
    function bindTips() {
        el.tip = $('frTip');
        if (!el.tip) return;
        var host = el.tip.parentNode;
        var show = function (text) { el.tip.textContent = text || paneTip(); };
        var pick = function (e) {
            var t = e.target.closest('[data-tip]');
            // ⚠️ Скрытая вкладка обязана молчать. Восстановление сохранённого
            // значения ползунка шлёт `input` ЕЩЁ ДО того, как человек что-то
            // трогал, и всплывает оно до своей вкладки: страница открывалась с
            // подсказкой про кисть под ползунками кожи.
            if (t && t.closest('.fr-tabpane[hidden]')) return;
            show(t ? t.dataset.tip : null);
        };
        // pointerover, а не pointerenter: нужен всплывающий вариант, иначе
        // переход между двумя ползунками не переключает текст.
        ['pointerover', 'focusin', 'input'].forEach(function (ev) {
            host.addEventListener(ev, pick);
        });
        // А возврат к сводке — только при уходе из ВСЕЙ панели: pointerout
        // между соседними ползунками стирал бы подсказку на каждом движении.
        host.addEventListener('pointerleave', function () { show(null); });
        host.addEventListener('focusout', function () { show(null); });
        show(null);
    }
    function paneTip() {
        var pane = document.querySelector('.fr-tabpane[data-pane]:not([hidden])');
        return (pane && pane.dataset.tip) || '';
    }
    function resetTip() { if (el.tip) el.tip.textContent = paneTip(); }

    // ── Вкладки рейки ───────────────────────────────────────────────────────
    function bindTabs() {
        var host = $('frTabs');
        if (!host) return;
        host.addEventListener('click', function (e) {
            var b = e.target.closest('[data-tab]');
            if (b) showTab(b.dataset.tab);
        });
        // Стрелками, как положено вкладкам: без этого клавиатура застревает
        // на первой и остальные пять недостижимы.
        host.addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            var all = Array.prototype.slice.call(host.querySelectorAll('[data-tab]'));
            var i = all.indexOf(document.activeElement);
            if (i < 0) return;
            var j = (i + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length;
            all[j].focus(); showTab(all[j].dataset.tab); e.preventDefault();
        });
    }
    function showTab(name) {
        var host = $('frTabs');
        if (!host) return;
        Array.prototype.forEach.call(host.querySelectorAll('[data-tab]'), function (b) {
            var on = b.dataset.tab === name;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        Array.prototype.forEach.call(document.querySelectorAll('.fr-tabpane'), function (p) {
            p.hidden = p.dataset.pane !== name;
        });
        // Уход со вкладки кисти гасит режим наведения: иначе человек, ушедший
        // крутить ползунки, продолжал бы ставить заплатки нажатием по снимку.
        if (name !== 'spot' && state.aim === 'heal') setAim(null);
        resetTip();
        stageOnce('tab_' + name);
    }
    /** Вкладка, которой на этом снимке нечего делать, объясняет почему. */
    function syncTabs() {
        var geo = state.faces.length > 0;
        if (el.eyesLocked) el.eyesLocked.hidden = geo;
        if (el.eyesBody) el.eyesBody.hidden = !geo;
        var lvl = $('frLevel');
        if (lvl) lvl.disabled = !geo;
    }

    // ── Сцена: сравнение, зум, наведение ────────────────────────────────────
    function bindStage() {
        var cmp = $('frCmp');
        cmp && cmp.addEventListener('click', function (e) {
            var b = e.target.closest('[data-cmp]');
            if (!b) return;
            setCmp(b.dataset.cmp);
            ae('tool_interact', { f: 'cmp', mode: b.dataset.cmp });
        });
        var zi = $('frZoomIn'), zo = $('frZoomOut');
        zi && zi.addEventListener('click', function () { stepZoom(1); });
        zo && zo.addEventListener('click', function () { stepZoom(-1); });

        if (!el.box) return;
        var dragging = false, mode = null, last = null;
        var boxPos = function (ev) {
            var r = el.box.getBoundingClientRect();
            var cx = (ev.touches ? ev.touches[0].clientX : ev.clientX);
            var cy = (ev.touches ? ev.touches[0].clientY : ev.clientY);
            return { x: cx - r.left, y: cy - r.top, w: r.width, h: r.height };
        };
        var down = function (ev) {
            var p = boxPos(ev);
            if (state.aim) { onAimClick(p); ev.preventDefault(); return; }
            if (state.zoom > 1) { mode = 'pan'; last = p; el.box.classList.add('dragging'); }
            else if (state.cmp === 'wipe') { mode = 'wipe'; setWipe(p.x / Math.max(1, p.w)); }
            else return;
            dragging = true; ev.preventDefault();
        };
        var move = function (ev) {
            var p = boxPos(ev);
            if (state.aim) { moveAimRing(p); return; }
            if (!dragging) return;
            if (mode === 'wipe') setWipe(p.x / Math.max(1, p.w));
            else if (mode === 'pan') {
                state.pan.x += p.x - last.x; state.pan.y += p.y - last.y;
                last = p; applyZoom();
            }
        };
        var up = function () { dragging = false; mode = null; el.box.classList.remove('dragging'); };
        el.box.addEventListener('mousedown', down);
        el.box.addEventListener('touchstart', down, { passive: false });
        // Слушатель ОДИН, на окне: тот же обработчик на коробке отрабатывал
        // второй раз на каждое движение мыши над кадром.
        window.addEventListener('mousemove', move);
        window.addEventListener('touchmove', move, { passive: true });
        window.addEventListener('mouseup', up);
        window.addEventListener('touchend', up);
        el.box.addEventListener('dblclick', function () { setZoom(1); });

        // Клавиатура: шторка объявлена как slider, значит обязана двигаться стрелками.
        el.wipe.addEventListener('keydown', function (e) {
            var step = e.shiftKey ? 0.10 : 0.02;
            if (e.key === 'ArrowLeft') { setWipe(state.wipe - step); e.preventDefault(); }
            else if (e.key === 'ArrowRight') { setWipe(state.wipe + step); e.preventDefault(); }
            else if (e.key === 'Home') { setWipe(0); e.preventDefault(); }
            else if (e.key === 'End') { setWipe(1); e.preventDefault(); }
        });
        setWipe(0.5);
    }

    function setCmp(mode) {
        state.cmp = mode;
        var cmp = $('frCmp');
        cmp && Array.prototype.forEach.call(cmp.querySelectorAll('[data-cmp]'), function (b) {
            var on = b.dataset.cmp === mode;
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        el.box.classList.toggle('no-wipe', mode !== 'wipe');
        setWipe(state.wipe);
    }

    /**
     * Положение реза.
     *
     * Рез живёт на ХОЛСТЕ, а ручка — на коробке, и при увеличении эти две
     * системы расходятся: холст сдвинут панорамированием и растянут зумом.
     * Поэтому доля коробки переводится в долю холста явно — без этого при
     * зуме шторка резала бы кадр не там, где нарисована линия.
     */
    function setWipe(v) {
        state.wipe = Math.max(0, Math.min(1, v));
        var pct = (state.wipe * 100).toFixed(2) + '%';
        el.wipe.style.left = pct;
        el.wipe.setAttribute('aria-valuenow', Math.round(state.wipe * 100));
        var t = state.cmp === 'after' ? 0 : (state.cmp === 'before' ? 1 : state.wipe);
        var boxW = el.box.clientWidth || 1;
        var onCanvas = state.zoom === 1 ? t : (t * boxW - state.pan.x) / (boxW * state.zoom);
        onCanvas = Math.max(0, Math.min(1, onCanvas));
        el.after.style.clipPath = 'inset(0 0 0 ' + (onCanvas * 100).toFixed(2) + '%)';
    }

    function stepZoom(dir) {
        var i = ZOOMS.indexOf(state.zoom);
        if (i < 0) i = 0;
        setZoom(ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, i + dir))]);
        stageOnce('zoom', { z: state.zoom });
    }
    function setZoom(z) {
        state.zoom = z;
        if (z === 1) state.pan = { x: 0, y: 0 };
        applyZoom();
        if (el.zoomVal) el.zoomVal.textContent = Math.round(z * 100) + '%';
        el.box && el.box.classList.toggle('zoomed', z > 1);
        var zi = $('frZoomIn'), zo = $('frZoomOut');
        if (zi) zi.disabled = z >= ZOOMS[ZOOMS.length - 1];
        if (zo) zo.disabled = z <= 1;
    }
    function applyZoom() {
        if (!el.pan || !el.box) return;
        var w = el.box.clientWidth || 1, h = el.box.clientHeight || 1;
        // Панорамирование ограничено кадром: пустое поле сбоку в увеличенном
        // виде читается как «картинка потерялась».
        var minX = w - w * state.zoom, minY = h - h * state.zoom;
        state.pan.x = Math.min(0, Math.max(minX, state.pan.x));
        state.pan.y = Math.min(0, Math.max(minY, state.pan.y));
        el.pan.style.transform = 'translate(' + state.pan.x + 'px,' + state.pan.y + 'px) scale(' + state.zoom + ')';
        setWipe(state.wipe);
    }

    // ── Наведение: указать лицо / поставить заплатку ────────────────────────
    function setAim(kind) {
        state.aim = kind;
        el.box && el.box.classList.toggle('aim', !!kind);
        var p = $('frPoint'), hb = $('frHealOn');
        if (p) p.setAttribute('aria-pressed', kind === 'point' ? 'true' : 'false');
        if (hb) {
            hb.setAttribute('aria-pressed', kind === 'heal' ? 'true' : 'false');
            hb.textContent = kind === 'heal' ? (I.heal_off || '') : (I.heal_on || '');
        }
        if (kind === 'heal') moveAimRing(null);
        if (el.aimHint) {
            el.aimHint.hidden = !kind;
            el.aimHint.textContent = kind === 'heal' ? (I.heal_prompt || '') : (kind ? (I.point_prompt || '') : '');
        }
    }
    function moveAimRing(p) {
        if (!el.aimRing) return;
        if (!p) { el.aimRing.style.width = el.aimRing.style.height = brushBoxPx() + 'px'; return; }
        var d = state.aim === 'heal' ? brushBoxPx() : 44;
        el.aimRing.style.width = el.aimRing.style.height = d + 'px';
        el.aimRing.style.left = p.x + 'px';
        el.aimRing.style.top = p.y + 'px';
    }
    /** Диаметр кисти В ПИКСЕЛЯХ КОРОБКИ — чтобы кольцо совпадало с тем, что
     *  реально закрасится, при любом увеличении. */
    function brushBoxPx() {
        var hs = $('frHealSize');
        var frac = (hs ? +hs.value : 18) / 1000;          // доля большей стороны кадра
        var boxW = el.box ? el.box.clientWidth : 320;
        var boxH = el.box ? el.box.clientHeight : 320;
        return Math.max(10, 2 * frac * Math.max(boxW, boxH) * state.zoom);
    }

    async function onAimClick(p) {
        var boxW = el.box.clientWidth || 1, boxH = el.box.clientHeight || 1;
        // Из коробки — в холст (снимаем панорамирование и зум).
        var nx = ((p.x - state.pan.x) / (boxW * state.zoom));
        var ny = ((p.y - state.pan.y) / (boxH * state.zoom));
        if (nx < 0 || ny < 0 || nx > 1 || ny > 1) return;

        if (state.aim === 'point') return pointAtFace(nx, ny);
        if (state.aim === 'heal') return healAt(nx, ny);
    }

    async function pointAtFace(nx, ny) {
        setAim(null);
        showVeil(I.searching || '', true, 0);
        try {
            var p = state.workPlanes;
            var res = await window.FRFind.around(state.workCanvas, p.w, p.h, nx, ny, {
                onStep: function (d, t) { veilProgress(d, t); },
            });
            ae('feature_use', { f: 'point_at_face', found: res.faces.length, via: res.via, tries: res.tries });
            if (!res.faces.length) {
                hideVeil();
                window.__toast && window.__toast(I.point_missed || '', 'warning');
                return;
            }
            applyFaces(res.faces, 'face');
        } catch (err) {
            ae('tool_error', { f: 'point_failed', msg: String(err && err.message).slice(0, 100) });
        }
        hideVeil();
        await apply();
    }

    async function healAt(nx, ny) {
        var hs = $('frHealSize');
        var r = (hs ? +hs.value : 18) / 1000;
        var src = invPoint(state.srcBitmap, nx, ny);
        state.healOps.push({ x: src.x, y: src.y, r: r });
        state.healedPlanes = null;
        syncHealControls();
        stageOnce('heal_used');
        await apply();

        // ⚠️ Мазок мог НЕ ЛЕЧЬ: у самого края кадра донора вокруг пятна просто
        // нет. Молчание здесь неотличимо от поломки — человек нажимает второй
        // раз в то же место и снова ничего не видит. Поэтому неудавшийся мазок
        // снимается со счётчика и называет причину.
        var landed = state.healedPlanes ? (state.healedPlanes.healed || 0) : 0;
        var missed = state.healOps.length - landed;
        ae('feature_use', { f: 'heal_spot', n: state.healOps.length, r: Math.round(r * 1000), missed: missed });
        if (missed > 0) {
            state.healOps.pop();
            state.healedPlanes = null;
            syncHealControls();
            window.__toast && window.__toast(I.heal_missed || '', 'warning');
            ae('tool_diagnostic', { issue: 'heal_no_donor' });
            await apply();
        }
    }

    function bindHeal() {
        var on = $('frHealOn'), undo = $('frHealUndo'), clear = $('frHealClear'), size = $('frHealSize');
        on && on.addEventListener('click', function () {
            if (!state.srcBitmap) return;
            setAim(state.aim === 'heal' ? null : 'heal');
        });
        undo && undo.addEventListener('click', async function () {
            if (!state.healOps.length) return;
            state.healOps.pop(); state.healedPlanes = null;
            syncHealControls(); await apply();
            ae('tool_interact', { f: 'heal_undo', n: state.healOps.length });
        });
        clear && clear.addEventListener('click', async function () {
            if (!state.healOps.length) return;
            state.healOps = []; state.healedPlanes = null;
            syncHealControls(); await apply();
            ae('tool_interact', { f: 'heal_clear' });
        });
        if (size) {
            window.__toolSettings && window.__toolSettings.bind(SLUG, size, 'heal_size', size.value);
            size.addEventListener('input', function () {
                var o = $('frHealSizeOut');
                if (o) o.textContent = size.value;
                moveAimRing(null);
            });
            var o0 = $('frHealSizeOut');
            if (o0) o0.textContent = size.value;
        }
        syncHealControls();
    }
    function syncHealControls() {
        var undo = $('frHealUndo'), clear = $('frHealClear');
        var n = state.healOps.length;
        if (undo) undo.disabled = !n;
        if (clear) clear.disabled = !n;
        // Счёт мазков — в подсказку ВКЛАДКИ, а не отдельной строкой: он и есть
        // то, что человеку нужно знать на этой вкладке прямо сейчас.
        var pane = document.querySelector('.fr-tabpane[data-pane="spot"]');
        if (pane) pane.dataset.tip = n ? fmt(I.heal_count || '', { n: n }) : (I.heal_hint || '');
        if (el.tip && pane && !pane.hidden) resetTip();
    }

    // ── Кадр: поворот, отражение, выравнивание ──────────────────────────────
    function bindFrame() {
        var l = $('frRotL'), r = $('frRotR'), f = $('frFlipH'),
            st = $('frStraight'), lvl = $('frLevel'), rs = $('frFrameReset');
        l && l.addEventListener('click', function () { rotate(-90); });
        r && r.addEventListener('click', function () { rotate(90); });
        f && f.addEventListener('click', function () {
            state.tf.flipH = !state.tf.flipH;
            f.setAttribute('aria-pressed', state.tf.flipH ? 'true' : 'false');
            ae('tool_interact', { f: 'flip' });
            reframe();
        });
        if (st) {
            st.addEventListener('input', function () {
                var o = $('frStraightOut');
                if (o) o.textContent = (+st.value).toFixed(1).replace(/\.0$/, '') + '°';
                state.tf.angle = +st.value;
                scheduleReframe();
            });
        }
        lvl && lvl.addEventListener('click', levelByEyes);
        rs && rs.addEventListener('click', function () {
            state.tf = { rot: 0, flipH: false, angle: 0 };
            syncFrameControls();
            ae('tool_interact', { f: 'frame_reset' });
            reframe();
        });
    }
    function rotate(deg) {
        state.tf.rot = ((state.tf.rot + deg) % 360 + 360) % 360;
        stageOnce('rotate', { deg: state.tf.rot });
        ae('tool_interact', { f: 'rotate', deg: state.tf.rot });
        reframe();
    }
    function syncFrameControls() {
        var f = $('frFlipH'), st = $('frStraight'), o = $('frStraightOut');
        if (f) f.setAttribute('aria-pressed', state.tf.flipH ? 'true' : 'false');
        if (st) st.value = state.tf.angle;
        if (o) o.textContent = (+state.tf.angle).toFixed(1).replace(/\.0$/, '') + '°';
    }

    /**
     * Пересборка кадра.
     *
     * Поворот меняет геометрию целиком, поэтому лицо ищется заново — иначе
     * маска осталась бы от прежней ориентации и ретушь легла бы на воздух.
     * Выравнивание на пол-градуса геометрию почти не двигает, но своей ветки
     * ему не дают намеренно: два пути «когда пересчитывать» разошлись бы
     * ровно там, где их перестанут проверять вместе.
     */
    var reframeTimer = null;
    function scheduleReframe() {
        clearTimeout(reframeTimer);
        reframeTimer = setTimeout(reframe, 220);
    }
    async function reframe() {
        if (!state.srcBitmap) return;
        clearTimeout(reframeTimer);
        prepareWorkCanvas();
        drawBefore();
        setZoom(state.zoom);
        state.ready = false; syncSave();
        await refresh(true);
    }

    /**
     * Выровнять по глазам.
     *
     * Никто из конкурентов этого не делает, а данные для этого у нас уже
     * есть: линия глаз известна с точностью до пикселя, и её наклон и есть
     * заваленный горизонт портрета. Правится в один щелчок вместо ловли
     * половины градуса ползунком.
     */
    async function levelByEyes() {
        if (!state.faces.length) return;
        // Самое КРУПНОЕ лицо: на групповом снимке горизонт задаёт тот, кто в
        // кадре главный, а не тот, кто попался детектору первым.
        var f = state.faces.slice().sort(function (a, b) { return b.box.w - a.box.w; })[0];
        var c = function (poly) {
            if (!poly || !poly.length) return null;
            var sx = 0, sy = 0;
            for (var i = 0; i < poly.length; i++) { sx += poly[i][0]; sy += poly[i][1]; }
            return [sx / poly.length, sy / poly.length];
        };
        var a = c(f.leftEye), b = c(f.rightEye);
        if (!a || !b) { window.__toast && window.__toast(I.no_level || '', 'warning'); return; }
        var p = state.workPlanes;
        // Угол считается в ПИКСЕЛЯХ, а не в долях: доли неквадратного кадра
        // растянуты по одной оси, и угол в них — не угол.
        var dx = (b[0] - a[0]) * p.w, dy = (b[1] - a[1]) * p.h;
        var deg = Math.atan2(dy, dx) * 180 / Math.PI;
        if (Math.abs(deg) < 0.25) { window.__toast && window.__toast(I.no_level || '', 'info'); return; }
        var want = Math.max(-15, Math.min(15, state.tf.angle - deg));
        state.tf.angle = want;
        syncFrameControls();
        ae('feature_use', { f: 'level_by_eyes', deg: +deg.toFixed(2) });
        window.__toast && window.__toast(fmt(I.levelled || '', { deg: (-deg).toFixed(1) }), 'success');
        await reframe();
    }

    // ── Выход из тупика «лицо не найдено» ───────────────────────────────────
    function bindRescue() {
        var p = $('frPoint'), c = $('frColorLane');
        p && p.addEventListener('click', function () {
            setAim(state.aim === 'point' ? null : 'point');
            ae('tool_interact', { f: 'rescue_point_arm' });
        });
        c && c.addEventListener('click', async function () {
            ae('tool_interact', { f: 'rescue_color_click' });
            if (colorLane()) await apply();
        });
    }

    // ── Вуаль и полоса ──────────────────────────────────────────────────────
    /** Следим за загрузкой модели ВСЕГДА — в т. ч. когда её греет прогрев,
     *  а файла ещё нет: иначе первая же загрузка после выбора файла показала
     *  бы пустую полосу с нуля, хотя байты уже качаются. */
    function watchModel() {
        if (!window.FRLandmarks || !window.FRLandmarks.onProgress) return;
        window.FRLandmarks.onProgress(function (p) {
            if (!el.veil || !el.veil.classList.contains('on')) return;
            if (p.phase === 'model') {
                // Единица измерения — из перевода: «МБ», «MB», «ميغابايت» и
                // «МБ» украинского пишутся по-разному, а зашитая строка
                // выглядела бы опечаткой ровно у тех, кто её прочтёт.
                var mb = function (b) { return (b / 1048576).toFixed(1); };
                el.veilText.textContent = I.model_dl || '';
                if (p.total > 0) {
                    veilProgress(p.loaded, p.total);
                    el.veilSub.textContent = fmt(I.model_size || ':got / :total', { got: mb(p.loaded), total: mb(p.total) });
                } else {
                    veilIdle();
                    el.veilSub.textContent = fmt(I.model_size_one || ':got', { got: mb(p.loaded) });
                }
            } else if (p.phase === 'esm') {
                veilIdle(); el.veilText.textContent = I.model_lib || ''; el.veilSub.textContent = '';
            } else if (p.phase === 'init') {
                veilIdle(); el.veilText.textContent = I.model_init || ''; el.veilSub.textContent = '';
            } else if (p.phase === 'failed') {
                veilIdle(); el.veilText.textContent = I.model_failed || ''; el.veilSub.textContent = '';
            }
        });
    }
    function showVeil(text, determinate, pct) {
        if (!el.veil) return;
        el.veil.classList.add('on');
        el.veilText.textContent = text || '';
        el.veilSub.textContent = '';
        if (determinate) { el.veilBar.classList.remove('idle'); el.veilFill.style.width = (pct || 0) + '%'; }
        else veilIdle();
    }
    function veilIdle() { el.veilBar && el.veilBar.classList.add('idle'); }
    function veilProgress(done, total) {
        if (!el.veilBar) return;
        el.veilBar.classList.remove('idle');
        el.veilFill.style.width = Math.max(2, Math.min(100, Math.round(100 * done / Math.max(1, total)))) + '%';
    }
    function hideVeil() { el.veil && el.veil.classList.remove('on'); }

    function setBusy(on, text) {
        if (!el.busy) return;
        el.busy.classList.toggle('on', !!on);
        if (text && el.busyText) el.busyText.textContent = text;
    }
    function renderFaceChips() {
        if (!el.faces) return;
        if (state.faces.length < 2) { el.faces.hidden = true; el.faces.innerHTML = ''; return; }
        el.faces.hidden = false;
        el.faces.innerHTML = '';
        var span = document.createElement('span');
        span.className = 'fr-chip';
        span.textContent = (I.faces_found || '').replace(':n', String(state.faces.length));
        el.faces.appendChild(span);
    }

    /**
     * Строка честности. Здесь тул говорит, чего он НЕ смог, и почему —
     * вместо того чтобы молча отдать серое пятно там, где информации о коже
     * в файле физически не осталось.
     */
    function showTruth(model) {
        if (!el.truth) return;
        // При упавшей модели «указать лицо» бессмысленно — детектора нет вовсе,
        // и кнопка обещала бы работу, которой не будет. Лейн по цвету остаётся:
        // ему модель не нужна.
        if (el.rescue) el.rescue.hidden = !!model;
        var pointBtn = $('frPoint');
        if (pointBtn) pointBtn.hidden = !!state.modelFailed;
        if (!model) {
            el.truth.className = 'fr-truth fr-truth-warn';
            // Причину называем ТУ, что была. «Лицо не найдено» при упавшей
            // модели — неверное объяснение, а оно хуже отсутствующего: человек
            // уйдёт искать другие фотографии вместо того, чтобы перезагрузить
            // страницу.
            if (state.modelFailed) {
                el.truth.textContent = I.truth_no_model || I.model_failed || '';
            } else {
                el.truth.innerHTML = '<b>' + esc(I.no_face || '') + '</b> ' +
                    esc(state.deepTried ? (I.truth_deep || I.no_face_hint || '') : (I.no_face_hint || ''));
            }
            return;
        }
        var parts = [];
        if (state.lane === 'color') parts.push(I.truth_color_lane || '');
        if (state.rotatedHint) parts.push(I.truth_rotated || '');
        var blown = (model.blownFrac || 0) * 100;

        // «Блик снят» нельзя писать там, где снимать было нечего: человек
        // сравнит до и после, не увидит разницы и решит, что тул сломан.
        // Долю тронутой кожи знаем точно — говорим правду.
        var touched = 0, skin = 0;
        if (model.spec && model.mask) {
            for (var i = 0; i < model.mask.length; i++) {
                if (model.mask[i] <= 0.5) continue;
                skin++;
                if (model.spec[i] > 0) touched++;
            }
        }
        if (skin > 200 && touched / skin < 0.04) {
            parts.push(I.truth_little || '');
            el.truth.className = 'fr-truth';
            el.truth.textContent = parts.join(' ');
            stageOnce('little_shine', { pct: +(100 * touched / Math.max(1, skin)).toFixed(1) });
            return;
        }
        if (blown >= 0.5) parts.push((I.truth_blown || '').replace(':pct', blown.toFixed(1)));
        // Разделимость — измеримый предел задачи: когда цвет кожи и цвет
        // света совпадают, блик неотделим НИКАКИМ алгоритмом.
        if ((model.separability || 0) < 0.06) parts.push(I.truth_hardlight || '');
        // Частота ФИЗИЧЕСКОГО предела — отдельным событием: она решает, стоит
        // ли вкладываться в дорисовывание выбитых мест.
        if (blown >= 0.5) stageOnce('limit_blown', { pct: +blown.toFixed(1) });
        if ((model.separability || 0) < 0.06) stageOnce('limit_hardlight', { sep: +(model.separability || 0).toFixed(3) });
        var warn = blown >= 0.5 || (model.separability || 0) < 0.06;
        if (!parts.length) parts.push(I.truth_ok || '');
        el.truth.className = 'fr-truth' + (warn ? ' fr-truth-warn' : '');
        el.truth.textContent = parts.join(' ');
    }

    /**
     * Кнопка сохранения обязана ГАСНУТЬ, пока сохранять нечего.
     *
     * Пока грузится модель и на снимке без лица тул сохранить не может, а
     * кнопка выглядела обычной активной: нажатие выходило первой строкой
     * onSave и не давало человеку ничего. Причина при этом уже написана и
     * переведена — она в строке честности над холстом, — но прочесть её
     * мешало именно то, что кнопка звала нажать.
     *
     * ⚠️ Что ИЗМЕНИЛОСЬ с появлением лейна без лица: снимок без лица теперь
     * тоже можно сохранить — на нём работают свет, тон и кисть. Значит
     * условие «есть модель» больше не годится, и готовность считается по
     * тому, есть ли ВООБЩЕ что отдать: разобранный кадр либо хоть одна
     * правка, которой геометрия не нужна.
     *
     * ⚠️ По state.busy не гасим: он поднимается на КАЖДЫЙ пересчёт
     * предпросмотра, то есть на каждое движение ползунка.
     */
    function hasEditWithoutFace() {
        var p = params();
        return !!(p.exposure || p.warmth || state.healOps.length ||
            state.tf.rot || state.tf.flipH || state.tf.angle);
    }
    function syncSave() {
        if (!el.save) return;
        var batch = isBatch();
        // В пачке кнопка живёт от ОЧЕРЕДИ, а не от показанного снимка: тот
        // может быть без лица, а остальные девять — с лицами.
        var can = batch
            ? (state.queue.length > 0 && !state.saving)
            : !!(state.srcBitmap && !state.saving && (state.ready || hasEditWithoutFace()));
        el.save.disabled = !can;
        setAria(el.save, !can);
        el.save.textContent = batch
            ? fmt(I.save_all || '', { n: state.queue.length })
            : (I.save || el.save.textContent);

        // Сравнение — только для одного снимка: «до/после» пачкой было бы
        // десятью файлами на один жест, чего браузер не даёт.
        if (el.compare) {
            el.compare.hidden = batch;
            var canCmp = !batch && !!(state.srcBitmap && !state.saving && window.FRCompare &&
                (state.ready || hasEditWithoutFace()));
            el.compare.disabled = !canCmp;
            setAria(el.compare, !canCmp);
        }
    }

    /**
     * ⚠️ У aria-disabled ДВА владельца: мы (готовность тула) и блок лимита
     * (стена капа помечает кнопку через paintButtons). Снимать чужую пометку
     * нельзя — иначе поднятая стена перестаёт читаться скринридером ровно в
     * тот момент, когда тул стал готов.
     */
    function setAria(btn, blocked) {
        if (blocked) { btn.setAttribute('aria-disabled', 'true'); return; }
        if (!btn.classList.contains('tl-btn-blocked')) btn.removeAttribute('aria-disabled');
    }

    // ── Выдача: формат, размер, качество ────────────────────────────────────
    function bindExport() {
        var f = el.fmt, s = el.size, q = el.quality;
        if (f) {
            window.__toolSettings && window.__toolSettings.bind(SLUG, f, 'out_fmt', f.value);
            f.addEventListener('change', function () {
                syncQualityVisible();
                stageOnce('fmt', { v: f.value });
            });
        }
        if (s) {
            window.__toolSettings && window.__toolSettings.bind(SLUG, s, 'out_size', s.value);
            // Потолок тира применяется ЯВНО: без этого выбор «Оригинал» молча
            // отдавал бы полный размер всем, и платить за него было бы не за что.
            if (window.__qualityTier) window.__qualityTier.gate(s, 'side');
            s.addEventListener('change', function () { stageOnce('size', { v: s.value }); });
        }
        if (q) {
            window.__toolSettings && window.__toolSettings.bind(SLUG, q, 'out_q', q.value);
            q.addEventListener('input', function () {
                if (el.qualityOut) el.qualityOut.textContent = q.value;
            });
            if (el.qualityOut) el.qualityOut.textContent = q.value;
        }
        syncQualityVisible();
    }
    function syncQualityVisible() {
        // PNG без потерь: ползунок качества там ни на что не влияет, и
        // оставленный на экране он обещает управление, которого нет.
        var png = el.fmt && el.fmt.value === 'png';
        if (el.qualityWrap) el.qualityWrap.hidden = !!png;
    }
    function outFormat() {
        var v = el.fmt ? el.fmt.value : 'jpeg';
        if (v === 'png') return { mime: 'image/png', ext: 'png', q: undefined };
        if (v === 'webp') return { mime: 'image/webp', ext: 'webp', q: (el.quality ? +el.quality.value : 94) / 100 };
        return { mime: 'image/jpeg', ext: 'jpg', q: (el.quality ? +el.quality.value : 94) / 100 };
    }
    /** Потолок длинной стороны: выбор человека ∩ потолок тарифа. Никогда не
     *  увеличиваем и никогда не режем МОЛЧА — гейт объявил оба ограничения. */
    function sideCeiling() {
        var chosen = Infinity;
        if (el.size && el.size.value !== 'original') chosen = parseInt(el.size.value, 10) || Infinity;
        var plan = Infinity;
        try {
            var qt = window.__qualityTier;
            if (qt && !(qt.isPro && qt.isPro())) plan = qt.cap ? qt.cap('side') : Infinity;
        } catch (_) { }
        return Math.min(chosen, plan, EXPORT_MAX);
    }

    /**
     * Разбор ОДНОГО файла с нуля: битмап → рабочая копия → лица → модель.
     * Нужен пачке, где показан один снимок, а обрабатывать надо все.
     * Возвращает model=null, если лица нет — это не ошибка, а исход.
     */
    async function analyzeFile(file) {
        // ⚠️ Своё условие функция обеспечивает САМА. В пакетном режиме кнопка
        // активна сразу (она зависит от очереди, а не от разбора показанного
        // кадра), и нажатие через полсекунды после выбора приходило сюда с
        // незагруженной моделью: detect бросал «detector not ready», а человек
        // читал «лицо не найдено» — про снимки, где лица были. ensure()
        // идемпотентен: на уже загруженной модели это один промах по кэшу.
        await window.FRLandmarks.ensure(CFG.sources || [], { numFaces: 6, mode: 'IMAGE', downloaderUrl: CFG.mdUrl });
        var bm = await orientedBitmap(file);
        var k = Math.min(1, WORK_MAX / Math.max(bm.width, bm.height));
        var w = Math.max(1, Math.round(bm.width * k)), h = Math.max(1, Math.round(bm.height * k));
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(bm, 0, 0, w, h);
        var planes = window.FREngine.toPlanes(ctx.getImageData(0, 0, w, h));
        // Пачка тоже идёт лестницей поиска: снимок с мелким лицом, который
        // раньше просто пропускался, теперь чаще обрабатывается — и именно
        // ради этого лестница писалась.
        var found = await window.FRFind.search(c, w, h, { deep: true });
        var faces = found.faces;
        if (!faces.length) return { bitmap: bm, planes: planes, faces: [], model: null };
        var opts = { faceWidth: faces[0].box.w };
        var model = window.FREngine.analyze(planes, faces, opts);
        if (!model || !model.ok) return { bitmap: bm, planes: planes, faces: faces, model: null };
        model.faceWidth = opts.faceWidth;
        return { bitmap: bm, planes: planes, faces: faces, model: model };
    }

    /**
     * Применение на ПОЛНОМ размере. Снимок не уменьшается сверх того, что
     * выбрал человек и разрешает тариф: отдавать 12-мегапиксельное фото
     * ужатым до превью — тихая порча результата.
     * Поля модели растягиваются (см. FREngine.rescaleModel).
     */
    function renderFull(bitmap, model, faces, workPlanes, ops) {
        var b = baseSize(bitmap);
        var longSide = Math.max(b.w, b.h);
        var side = Math.min(longSide, sideCeiling());
        var k = side / longSide;
        var w = Math.max(1, Math.round(b.w * k)), h = Math.max(1, Math.round(b.h * k));
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        paintTransformed(ctx, bitmap, w, h);
        var planes = window.FREngine.toPlanes(ctx.getImageData(0, 0, w, h));
        // Мазки кисти переигрываются НА ПОЛНОМ РАЗМЕРЕ: их доли кадра от
        // разрешения не зависят, а результат лучше — донор берётся из
        // настоящих пикселей, а не из превью.
        if (ops && ops.length) planes = window.FRHeal.apply(planes, ops, { mask: null });
        var m = model;
        if (model && (w !== workPlanes.w || h !== workPlanes.h)) {
            m = window.FREngine.rescaleModel(model, w, h, workPlanes.w, workPlanes.h);
        }
        // Поле блика пересчитывать НЕ надо — оно гладкое и растянулось вместе
        // с моделью; но если его ещё не считали, deshine сделает это сам.
        var res = pipeline(planes, m, faces, params(), model ? model.faceWidth : 0.35);
        ctx.putImageData(window.FREngine.toImageData(res), 0, 0);
        return { canvas: c, w: w, h: h, capped: side < longSide };
    }

    /** Холст → файл выбранного формата с перенесённым EXIF исходника. */
    async function encode(canvas, srcFile) {
        var f = outFormat();
        var blob = await new Promise(function (r) { canvas.toBlob(r, f.mime, f.q); });
        // WebP есть не везде (Safari до 14, старые сборки WebView): холст
        // молча отдаёт PNG вместо отказа, а бывает и null. Проверяем ТИП, а
        // не факт возврата — иначе человек получит .webp, внутри которого PNG.
        if (!blob) throw new Error('encode-failed');
        if (blob.type !== f.mime) {
            ae('tool_diagnostic', { issue: 'format_unsupported', want: f.mime, got: blob.type });
        }
        // Дата съёмки, камера и ориентация обязаны пережить ретушь: холст не
        // переносит EXIF вообще, а галерея расставляет снимки без даты по дате
        // СОХРАНЕНИЯ. Перенос не имеет права стоить результата — модуль при
        // любой осечке возвращает файл как есть.
        if (window.FRExifCarry) blob = await window.FRExifCarry.carry(blob, srcFile, CFG.exifUrl, ae);
        return blob;
    }

    // ── Сохранение: один снимок ─────────────────────────────────────────────
    async function onSave() {
        if (state.saving) return;
        if (isBatch()) return onSaveBatch();
        if (!state.srcBitmap) return refuseSave();
        if (!state.ready && !hasEditWithoutFace()) return refuseSave();

        // Кап: единица — ФАЙЛ. Повторное сохранение того же снимка с другой
        // силой в тот же день бесплатно (ключ проштампован при добавлении).
        //
        // ⚠️ Проверка капа идёт ДО подъёма state.saving. Наоборот было
        // нельзя: отказ выходит через return мимо finally, и кнопка глохла бы
        // навсегда — стена, которая ломает тул, хуже стены.
        var run = null;
        if (window.__toolLimit && window.__toolLimit.begin) {
            run = window.__toolLimit.begin(SLUG, state.limitKey);
            if (!run) return;   // стену показал сам примитив
        }

        state.saving = true; syncSave();
        setBusy(true, I.applying || '');
        try {
            var out = renderFull(state.srcBitmap, state.model, state.faces, state.workPlanes, mappedOps());
            var blob = await encode(out.canvas, state.file);
            var ok = window.__downloadFile ? window.__downloadFile(blob, outputName(state.file, '-retouch')) : false;
            if (ok !== false) {
                run && run.commit && run.commit();
                window.__toast && window.__toast(I.saved || '', 'success');
                window.__aeToolComplete && window.__aeToolComplete({
                    slug: SLUG, w: out.w, h: out.h, sizeKB: Math.round(blob.size / 1024),
                    lane: state.lane, heal: state.healOps.length, capped: out.capped ? 1 : 0,
                    shine: Math.round(params().shine * 100),
                });
            } else {
                run && run.abort && run.abort();
            }
        } catch (err) {
            run && run.abort && run.abort();
            window.__handleError && window.__handleError(err, SLUG);
            ae('tool_error', { f: 'export_failed', msg: String(err && err.message).slice(0, 120) });
        } finally {
            state.saving = false;
            syncSave();
            setBusy(false);
        }
    }

    /** Отказ обязан НАЗВАТЬ причину: молчание неотличимо от поломки. */
    function refuseSave() {
        var why = state.srcBitmap ? (I.no_face || '') : (I.err_read || '');
        if (why && window.__toast) window.__toast(why, 'warning');
        ae('tool_diagnostic', { issue: 'save_before_ready', hasFile: !!state.srcBitmap });
    }

    // ── Сохранение: пачка ───────────────────────────────────────────────────
    /**
     * Почему именно так.
     *
     * · **Выдача — через `__batchDeliver.openBatch`, и он ПЕРВЫЙ await.**
     *   Папка живёт на пользовательской активации, а любой await до неё её
     *   тратит. Файлы производятся по одному, поэтому в памяти лежит один —
     *   держать пачку целиком нельзя: живых blob-байт больше ~2 ГиБ (на 8 ГБ
     *   ОЗУ — 1.6) браузер перестаёт отдавать, РАПОРТУЯ УСПЕХ.
     *
     * · **Ворота — на всю очередь, списание — пофайловое.** Пропускает или
     *   отвергает пачку целиком (precheck выше), как у всех пакетных тулов;
     *   но платит она только за ВЫДАННОЕ: снимок без лица слота не стоит,
     *   там abort(), а не commit(). Одним begin() на всю очередь так не
     *   получится — он списывает по числу свежих ключей, не по числу выдач.
     *
     * · **Битмап каждого снимка закрывается сразу.** 12 Мп — это ~48 МБ
     *   декодированных пикселей; десять таких, оставленных живыми, дают
     *   полгигабайта, которых у телефона нет.
     *
     * ⚠️ Поворот, выравнивание и мазки кисти к пачке НЕ применяются: они
     * относятся к ОДНОМУ показанному снимку, и молча перенести чужой поворот
     * на десять чужих фотографий было бы порчей девяти из них.
     */
    async function onSaveBatch() {
        if (!state.queue.length || state.saving) return;
        if (!window.__batchDeliver) { refuseSave(); return; }

        // ⚠️ Ворота на ВСЮ очередь, как у остальных пакетных тулов: очередь
        // либо влезает в остаток дня, либо нет. Соблазн отдать «сколько
        // влезет» был, и он неверен дважды: общий блок лимита уже пометил
        // кнопку заблокированной (paintButtons на всю очередь), то есть
        // частичная выдача противоречила бы тому, что человек ВИДИТ; и
        // человек, выучивший правило на /join, встретил бы здесь другое.
        // Убрать лишние снимки он может — у каждой миниатюры есть ×, и
        // precheck пересчитывается на удалении.
        try {
            if (window.__toolLimit && window.__toolLimit.precheck &&
                !window.__toolLimit.precheck(SLUG, state.queue.map(function (q) { return q.file; }))) {
                return;   // стену показал примитив
            }
        } catch (_) { }

        state.saving = true; syncSave();
        var batch = null, ok = 0, noFace = 0, failed = 0, stoppedByCap = false;
        var savedTf = state.tf;
        try {
            batch = await window.__batchDeliver.openBatch({
                slug: SLUG,
                total: state.queue.length,
                zipName: 'face-retouch.zip',
            });
            if (!batch) { return; }   // человек отменил выбор папки

            // Пачка идёт БЕЗ преобразования кадра — см. шапку.
            state.tf = { rot: 0, flipH: false, angle: 0 };

            for (var i = 0; i < state.queue.length; i++) {
                var item = state.queue[i];
                setBusy(true, window.FRLandmarks.loaded()
                    ? fmt(I.batch_working || '', { i: i + 1, n: state.queue.length })
                    : (I.loading || ''));
                await new Promise(function (r) { requestAnimationFrame(function () { setTimeout(r, 0); }); });

                var run = null;
                if (window.__toolLimit && window.__toolLimit.begin) {
                    run = window.__toolLimit.begin(SLUG, item.limitKey);
                    if (!run) { stoppedByCap = true; break; }   // стену показал примитив
                }

                var got = null;
                try {
                    got = await analyzeFile(item.file);
                    if (!got.model) {
                        // Лица нет — обрабатывать нечего. Слот за это не берём.
                        item.status = 'skip'; noFace++;
                        run && run.abort && run.abort();
                    } else {
                        var out = renderFull(got.bitmap, got.model, got.faces, got.planes, null);
                        var blob = await encode(out.canvas, item.file);
                        await batch.write(outputName(item.file, '-retouch'), blob);
                        item.status = 'ok'; ok++;
                        run && run.commit && run.commit();
                    }
                } catch (err) {
                    item.status = 'skip'; failed++;
                    run && run.abort && run.abort();
                    ae('tool_error', { f: 'batch_item_failed', msg: String(err && err.message).slice(0, 100) });
                } finally {
                    if (got && got.bitmap && got.bitmap.close) { try { got.bitmap.close(); } catch (_) { } }
                }
                markThumb(i, item.status);
            }

            if (ok) await batch.finish();
            else if (batch.dispose) { try { batch.dispose(); } catch (_) { } }

            reportBatch(ok, noFace, failed, stoppedByCap);
        } catch (err) {
            window.__handleError && window.__handleError(err, SLUG);
            ae('tool_error', { f: 'batch_failed', msg: String(err && err.message).slice(0, 120) });
        } finally {
            state.tf = savedTf;
            state.saving = false;
            syncSave();
            setBusy(false);
            renderStrip();
        }
    }

    /** Итог пачки словами: сколько вышло, сколько пропущено и ПОЧЕМУ. */
    function reportBatch(ok, noFace, failed, stoppedByCap) {
        var n = state.queue.length;
        if (!ok) {
            // Причину называем ТУ, что была. «Лицо не найдено» при нуле
            // пропусков по лицу — неверное объяснение, а оно хуже
            // отсутствующего: человек уйдёт искать другие фотографии.
            var line = noFace > 0 ? (I.batch_none || I.no_face || '') : (I.batch_failed || '');
            window.__toast && window.__toast(line, 'warning');
        } else {
            var done = stoppedByCap
                ? fmt(I.batch_stopped || '', { ok: ok, n: n })
                : fmt(I.batch_done || '', { ok: ok, n: n });
            window.__toast && window.__toast(done, stoppedByCap ? 'warning' : 'success');
            if (noFace + failed > 0 && !stoppedByCap) {
                // Второй строкой, а не в той же: причина пропуска — своя мысль,
                // и склеенная с цифрой она читается как одно сообщение об ошибке.
                setTimeout(function () {
                    window.__toast && window.__toast(fmt(I.batch_no_face || '', { n: noFace + failed }), 'info');
                }, 900);
            }
        }
        ae('feature_use', { f: 'batch_done', n: n, ok: ok, noFace: noFace, failed: failed, cap: stoppedByCap ? 1 : 0 });
        if (ok) window.__aeToolComplete && window.__aeToolComplete({ slug: SLUG, batch: n, ok: ok });
    }

    // ── Сохранение: «до/после» одной картинкой ──────────────────────────────
    /**
     * Своё нажатие, а не второй файл рядом с обычным сохранением: браузер
     * отдаёт ОДИН файл на жест, а лишний в том же жесте отравляет вкладку —
     * дальше молча теряются даже одиночные загрузки. Поэтому здесь ровно один
     * файл, и это склейка, а не два снимка.
     *
     * Кап: тот же ключ файла. Значит человек, уже сохранивший этот снимок
     * сегодня, получает сравнение бесплатно — нового материала он не берёт.
     */
    async function onCompare() {
        if (state.saving) return;
        if (!state.srcBitmap || !window.FRCompare) return refuseSave();
        if (!state.ready && !hasEditWithoutFace()) return refuseSave();

        var run = null;
        if (window.__toolLimit && window.__toolLimit.begin) {
            run = window.__toolLimit.begin(SLUG, state.limitKey);
            if (!run) return;
        }

        state.saving = true; syncSave();
        setBusy(true, I.applying || '');
        try {
            var after = renderFull(state.srcBitmap, state.model, state.faces, state.workPlanes, mappedOps());
            // «До» берём в ТОМ ЖЕ размере, что и «после», и с ТЕМ ЖЕ
            // преобразованием кадра: половины разного размера — это уже не
            // сравнение, а повёрнутая половина рядом с неповёрнутой — не
            // сравнение вдвойне.
            var before = document.createElement('canvas');
            before.width = after.w; before.height = after.h;
            paintTransformed(before.getContext('2d'), state.srcBitmap, after.w, after.h);

            var sheet = window.FRCompare.build(before, after.canvas, {
                labelA: I.tag_before || '', labelB: I.tag_after || '',
            });
            var blob = await new Promise(function (r) { sheet.toBlob(r, 'image/jpeg', 0.92); });
            if (!blob) throw new Error('encode-failed');

            var ok = window.__downloadFile ? window.__downloadFile(blob, outputName(state.file, '-before-after')) : false;
            if (ok !== false) {
                run && run.commit && run.commit();
                window.__toast && window.__toast(I.compare_saved || I.saved || '', 'success');
                ae('feature_use', { f: 'compare_saved', w: sheet.width, h: sheet.height, sizeKB: Math.round(blob.size / 1024) });
            } else {
                run && run.abort && run.abort();
            }
        } catch (err) {
            run && run.abort && run.abort();
            window.__handleError && window.__handleError(err, SLUG);
            ae('tool_error', { f: 'compare_failed', msg: String(err && err.message).slice(0, 120) });
        } finally {
            state.saving = false;
            syncSave();
            setBusy(false);
        }
    }

    /** Имя выдачи — от исходного, с пометкой, через общий примитив. */
    function outputName(file, suffix) {
        var base = ((file && file.name) || 'photo').replace(/\.[^.]+$/, '');
        var ext = suffix === '-before-after' ? 'jpg' : outFormat().ext;
        if (window.__outputName) {
            try {
                // join(base, ext) принимает СТРОКИ. Массив и объект давали
                // «имя,retouch.[object Object]» — файл без расширения,
                // который система не свяжет ни с одной программой.
                return window.__outputName.join(base + suffix, ext);
            } catch (_) { /* примитив не подгрузился — падать из-за имени нельзя */ }
        }
        return base.replace(/[\\/:*?"<>|]+/g, '_') + suffix + '.' + ext;
    }
})();
